import { Link } from "react-router-dom";
import {
  ShieldCheck,
  Heart,
  Sparkles,
  ArrowUpRight,
  MessageCircle,
  Quote,
  Target,
} from "lucide-react";
import { WHATSAPP_LINK, BRAND } from "../brand";
import { Seo } from "../components/Seo";

const ABOUT_IMG = "/brand/cooking-stove.png";

const About = () => {
  return (
    <main data-testid="about-page" className="bg-[#f7f5f2]">
      <Seo
        title="About ooloo — Kitchen Help in No Time | Lucknow"
        description="ooloo is Lucknow's on-demand kitchen partner — verified executives, hygiene-first SOPs, your kitchen, your ingredients, your taste. Serving Gomtinagar, Gomtinagar Extension, Sushant Golf City."
        path="/about"
      />
      <section className="max-w-7xl mx-auto px-6 md:px-10 pt-16 pb-12">
        <p className="eyebrow rise">About ooloo</p>
        <h1 className="font-display mt-5 text-5xl md:text-7xl font-black tracking-tighter leading-[0.95] max-w-4xl rise rise-1">
          Kitchen help
          <span className="italic font-serif font-light text-[#c45b3e]"> in no time</span>.
        </h1>
        <p className="mt-6 max-w-2xl text-lg text-[#5c5a56] leading-relaxed rise rise-2">
          The name <span className="font-display font-bold text-[#0a0a0a]">ooloo</span>{" "}
          looks a lot like <span className="font-mono font-bold text-[#0a0a0a]">00:00</span>{" "}
          — a clock. That&apos;s the promise: instant, on-demand, on the dot.
        </p>
        <p className="mt-6 font-serif italic text-2xl text-[#c45b3e] rise rise-3">
          {BRAND.slogan}
        </p>
      </section>

      {/* STORY */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 pb-20">
        <div className="grid lg:grid-cols-12 gap-8">
          <div className="lg:col-span-7 bento-card overflow-hidden p-0 relative">
            <img
              src={ABOUT_IMG}
              alt="An ooloo executive cooking at home"
              className="w-full h-[460px] md:h-[640px] object-cover"
            />
            <div className="absolute bottom-6 left-6 right-6 bg-white/95 backdrop-blur rounded-2xl p-5 border border-[#e5e2dc]">
              <p className="eyebrow">tagline</p>
              <p className="mt-2 font-display text-xl md:text-2xl font-extrabold tracking-tight text-[#0a0a0a]">
                0% kitchen stress. 100% home food.
              </p>
            </div>
          </div>
          <div className="lg:col-span-5 flex flex-col gap-5">
            <div className="bento-card p-8 md:p-10">
              <Quote size={28} className="text-[#c45b3e]" />
              <p className="mt-5 font-display text-2xl md:text-3xl font-bold tracking-tight leading-snug text-[#0a0a0a]">
                &ldquo;Great food brings families together, but the endless chopping,
                cooking, and the mountain of dirty dishes? Not so much.&rdquo;
              </p>
            </div>
            <div className="dark-card p-8 md:p-10">
              <p className="eyebrow text-white/60">Where we are</p>
              <p className="mt-3 font-display text-3xl font-extrabold tracking-tight text-white">
                {BRAND.city}
              </p>
              <p className="mt-2 text-white/70">
                {BRAND.zones.join(" · ")} — and growing one neighbourhood at a time.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* STORY BLOCK */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 pb-20">
        <div className="grid lg:grid-cols-12 gap-8">
          <div className="lg:col-span-4">
            <p className="eyebrow">Our story</p>
          </div>
          <div className="lg:col-span-8 space-y-5 text-lg leading-relaxed text-[#0a0a0a] font-medium">
            <p>
              We started <span className="font-display font-bold text-[#0a0a0a]">ooloo</span>{" "}
              with a simple mission: to take the daily stress out of home cooking. We
              realised that busy professionals, tired parents, and party hosts often lose
              hours grinding away in the kitchen when they&apos;d rather be enjoying their
              food and spending time with loved ones.
            </p>
            <p>
              So, we created ooloo — your instant, on-demand kitchen sidekick. We bring
              reliable, professional kitchen support right to your doorstep, exactly when
              you need it.
            </p>
          </div>
        </div>
      </section>

      {/* MISSION */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 pb-20">
        <div className="dark-card grain p-10 md:p-16">
          <div className="relative grid lg:grid-cols-12 gap-8">
            <div className="lg:col-span-4">
              <Target size={36} strokeWidth={1.6} className="text-[#c45b3e]" />
              <p className="eyebrow text-white/60 mt-6">Our mission</p>
            </div>
            <div className="lg:col-span-8">
              <h2 className="font-display text-3xl md:text-5xl font-black tracking-tighter leading-[0.95]">
                Make home-cooked meals
                <span className="italic font-serif font-light text-[#c45b3e]"> completely effortless</span>.
              </h2>
              <p className="mt-6 text-white/75 text-lg leading-relaxed max-w-2xl">
                Whether you need a quick hand with daily chopping, a proper meal cooked
                from scratch, or extra support for a massive festive feast, ooloo ensures
                your kitchen runs smoothly without you lifting a finger.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* PROMISE */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 pb-24">
        <p className="eyebrow">The ooloo promise</p>
        <h2 className="font-display mt-4 text-4xl md:text-6xl font-black tracking-tighter leading-[0.95] max-w-3xl">
          Built on
          <span className="italic font-serif font-light text-[#c45b3e]"> trust</span>.
        </h2>
        <p className="mt-5 max-w-2xl text-[#5c5a56] text-lg leading-relaxed">
          Because we operate inside your home, your safety and satisfaction are our
          absolute priorities.
        </p>

        <div className="mt-12 grid md:grid-cols-3 gap-5">
          {[
            {
              icon: ShieldCheck,
              t: "Verified professionals",
              d: "Every ooloo kitchen executive undergoes strict background checks and rigorous hygiene training.",
            },
            {
              icon: Heart,
              t: "Your kitchen, your taste",
              d: "We don't bring pre-made food. We cook using your ingredients, your oil, and your spice levels — 100% healthy, customised home food.",
            },
            {
              icon: Sparkles,
              t: "Hygiene first",
              d: "Cleanliness is non-negotiable. Our team follows strict sanitisation protocols so your kitchen remains spotless.",
            },
          ].map((v, i) => (
            <div key={v.t} className={`bento-card p-8 rise rise-${i + 1}`}>
              <v.icon size={28} strokeWidth={1.6} className="text-[#c45b3e]" />
              <h3 className="mt-6 font-display text-2xl font-extrabold tracking-tight">
                {v.t}
              </h3>
              <p className="mt-2 text-[#5c5a56] leading-relaxed">{v.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* STATS BAND */}
      <section className="bg-[#efebe6] py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid md:grid-cols-4 gap-8">
          {[
            { k: "2,400+", v: "homes served weekly" },
            { k: "48", v: "trained kitchen executives" },
            { k: "30 min", v: "average doorstep time" },
            { k: "4.9★", v: "average rating since launch" },
          ].map((s) => (
            <div key={s.k}>
              <p className="font-display text-5xl md:text-6xl font-black tracking-tighter text-[#0a0a0a]">
                {s.k}
              </p>
              <p className="mt-2 text-[#5c5a56] text-sm uppercase tracking-widest font-bold">
                {s.v}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-24">
        <div className="dark-card grain p-10 md:p-16">
          <div className="relative grid lg:grid-cols-2 gap-10 items-end">
            <h2 className="font-display text-4xl md:text-6xl font-black tracking-tighter leading-[0.95]">
              Come meet our kitchen.
            </h2>
            <div className="flex flex-wrap gap-4 lg:justify-end">
              <a
                href={WHATSAPP_LINK}
                target="_blank"
                rel="noreferrer"
                data-testid="about-whatsapp-cta"
                className="btn-whatsapp"
              >
                <MessageCircle size={18} strokeWidth={2.5} /> Say hi on WhatsApp
              </a>
              <Link
                to="/contact"
                data-testid="about-contact-link"
                className="bg-white text-[#0a0a0a] hover:bg-[#f7f5f2] px-8 py-4 rounded-full font-bold transition inline-flex items-center gap-2"
              >
                Contact <ArrowUpRight size={18} />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default About;
